import { Fragment, type ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { Popover } from "./Popover";
import { Text } from "./Text";

export interface SelectOption {
  value: string;
  label: string;
  /** Optional trailing hint (e.g. a credit cost). */
  hint?: string;
  /** Optional leading icon, shown in the trigger and the option row. */
  icon?: ReactNode;
  /** When true the option is shown greyed and can't be selected. */
  disabled?: boolean;
  /**
   * Optional section label. When set, consecutive options sharing a group get a
   * small header above the first of them (e.g. "Image" / "Video"). Keep grouped
   * options contiguous so each group renders once.
   */
  group?: string;
}

export interface SelectFieldProps {
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  /** Extra classes merged onto the trigger button (e.g. a card-style picker). */
  triggerClassName?: string;
  /**
   * Extra classes on the popover root wrapper. The wrapper is `inline-flex` by
   * default (content width); pass `w-full` here to let a `w-full` trigger fill
   * its container.
   */
  className?: string;
}

/**
 * A compact popover single-select for composer controls (style / aspect /
 * quality / duration). Trigger pill matches the model picker; the panel lists
 * options with a check on the active one. Monochrome by construction.
 */
export function SelectField({
  label,
  value,
  options,
  onChange,
  placeholder,
  triggerClassName,
  className,
}: SelectFieldProps) {
  const active = options.find((o) => o.value === value);
  return (
    <Popover
      label={label}
      side="bottom"
      align="start"
      className={className}
      panelClassName="w-[240px]"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            "inline-flex h-9 w-full items-center justify-between gap-2 rounded-md bg-bubble px-3 font-sans text-[13px] font-medium text-ink outline-none transition-colors duration-150 ease-house hover:bg-tint focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring)]",
            triggerClassName,
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            {active?.icon}
            <span className="truncate">{active ? active.label : (placeholder ?? label)}</span>
          </span>
          <ChevronDownIcon />
        </button>
      )}
    >
      {({ close }) => (
        <div role="listbox" aria-label={label} className="flex min-h-0 flex-col overflow-y-auto p-1">
          {options.map((option, i) => {
            const selected = option.value === value;
            const showHeader = !!option.group && option.group !== options[i - 1]?.group;
            return (
              <Fragment key={option.value}>
                {showHeader ? (
                  <Text
                    variant="body"
                    as="div"
                    className="px-3 pb-1 pt-2.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-faint first:pt-1"
                  >
                    {option.group}
                  </Text>
                ) : null}
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  disabled={option.disabled}
                  onClick={() => {
                    onChange(option.value);
                    close();
                  }}
                  className="flex w-full items-center gap-2.5 rounded-sm px-3 py-2 text-left outline-none transition-colors duration-150 ease-house hover:bg-line-soft focus-visible:bg-line-soft disabled:pointer-events-none disabled:opacity-40"
                >
                  <CheckIcon className={cn("text-ink", selected ? "opacity-100" : "opacity-0")} />
                  {option.icon}
                  <span className="min-w-0 flex-1 truncate font-sans text-[13.5px] font-medium text-ink">
                    {option.label}
                  </span>
                  {option.hint ? (
                    <Text variant="body" as="span" className="shrink-0 text-[11.5px] text-ink-faint">
                      {option.hint}
                    </Text>
                  ) : null}
                </button>
              </Fragment>
            );
          })}
        </div>
      )}
    </Popover>
  );
}

function ChevronDownIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="size-3.5 shrink-0 text-ink-faint"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn("size-4 shrink-0", className)}
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
